import { v5 as uuidv5 } from "uuid";

/**
 * The element key carried in a localization tag:
 *
 *     element_key = [{surface}.]{core}[.{ordinal}]
 *
 * Core is a Beefree module uuid, an HTML-editor minted short id, or a whole field's
 * name. Ordinal is the 1-based position of a line inside a line-capable Beefree module,
 * always present for those module types even when there is only one line — otherwise
 * prepending a second line would rename the first line's key.
 *
 * Surface is set on A/B arm keys and dynamic-content variant keys only, so arms and
 * variants seeded from one body never share rows.
 *
 * A minted core is meaningless on purpose. Keys that encode a position silently
 * re-point at different lines when content is inserted above them.
 */

const KEY_PREFIX = "k";
const KEY_LENGTH = 6;
const BASE = 36;
const KEY_SPACE = BASE ** KEY_LENGTH;

const VARIANT_SURFACE_PREFIX = "variant-";
const SLOT_SURFACE_PREFIX = "slot-";
const SURFACE_HEX_LENGTH = 8;
const SURFACE_HEX = `[0-9a-f]{${SURFACE_HEX_LENGTH}}`;
const SURFACE_RE = new RegExp(
  `^(?:${VARIANT_SURFACE_PREFIX}|${SLOT_SURFACE_PREFIX})${SURFACE_HEX}$`,
);

function surfaceHex(id: string): string {
  return id.replace(/-/g, "").slice(0, SURFACE_HEX_LENGTH).toLowerCase();
}

export function variantSurface(variantId: string): string {
  return VARIANT_SURFACE_PREFIX + surfaceHex(variantId);
}

/**
 * The surface a dynamic-content variant stamps under. A branch is shared by every placement of its condition and one
 * placement's variants are seeded from one module, so only the pair is unique; a content row id changes on every save.
 */
export function slotSurface({
  usageId,
  branchId,
}: {
  usageId: string;
  branchId: string;
}): string {
  return (
    SLOT_SURFACE_PREFIX +
    surfaceHex(uuidv5(`slot-surface:${usageId}:${branchId}`, uuidv5.URL))
  );
}

/** Whether a key belongs to a dynamic-content variant rather than the body or an arm. */
export function isSlotVariantKey(elementKey: string): boolean {
  return (
    parseElementKey(elementKey).surface?.startsWith(SLOT_SURFACE_PREFIX) ??
    false
  );
}

/**
 * The surface an A/B arm's bare fields (subject, preheader, SMS body) stamp under, or none for base copy.
 * The renderer and the pre-send assertion both derive it here, so the key the assertion checks is the key the render looks up.
 */
export function metadataSurface(
  variantId: string | null | undefined,
): string | undefined {
  return variantId == null ? undefined : variantSurface(variantId);
}

export function withSurface(surface: string | undefined, core: string): string {
  return surface === undefined ? core : `${surface}.${core}`;
}

/**
 * `tags.ts` builds the tag's argument pattern from this, so the charset written and the
 * charset parsed cannot drift. No quote, brace or whitespace character, which is what
 * lets a key ride in a tag as a bare unquoted token: a quoted key would terminate the
 * `href="…"` that an attribute-slot tag renders inside.
 */
export const ELEMENT_KEY_CHARS = "A-Za-z0-9._-";

const ELEMENT_KEY_RE = new RegExp(`^[${ELEMENT_KEY_CHARS}]+$`);

/**
 * A charset check rather than a grammar check: keys reach the reader that this module
 * did not mint — a content block's own keys, or one derived from a Beefree module uuid —
 * so the shape of the core is not something to assert on.
 */
export function isElementKey(value: string): boolean {
  return value.length > 0 && ELEMENT_KEY_RE.test(value);
}

export function buildElementKey(core: string, ordinal?: number): string {
  return ordinal === undefined ? core : `${core}.${ordinal}`;
}

export type ParsedElementKey = {
  surface?: string;
  core: string;
  ordinal?: number;
};

/**
 * Total by design: an unminted key comes back as something rather than throwing at
 * render time. Surface and ordinal are read off the two ends, so a dotted core survives.
 */
export function parseElementKey(elementKey: string): ParsedElementKey {
  const segments = elementKey.split(".");
  const first = segments[0];
  const hasSurface =
    segments.length > 1 && first !== undefined && SURFACE_RE.test(first);
  const rest = hasSurface ? segments.slice(1) : segments;
  const last = rest.at(-1);
  const hasOrdinal =
    rest.length > 1 && last !== undefined && /^\d+$/.test(last);
  return {
    ...(hasSurface && first !== undefined ? { surface: first } : {}),
    core: (hasOrdinal ? rest.slice(0, -1) : rest).join("."),
    ...(hasOrdinal && last !== undefined ? { ordinal: Number(last) } : {}),
  };
}

/** Presentation and field classification only. The whole key is the identity. */
export function keyCore(elementKey: string): string {
  return parseElementKey(elementKey).core;
}

/**
 * `crypto.getRandomValues` rather than `Math.random` — not for secrecy, but because
 * this lib runs in the browser, in Node and inside a Jest module registry, and the Web
 * Crypto API is the one generator all three agree on.
 *
 * Rejection sampling keeps the distribution flat: `% KEY_SPACE` of a 32-bit draw would
 * make the first ~1.4% of the space twice as likely, and biased keys collide sooner
 * than the birthday bound predicts.
 */
function randomKeyIndex(): number {
  const limit = Math.floor(0x1_0000_0000 / KEY_SPACE) * KEY_SPACE;
  const buffer = new Uint32Array(1);
  for (;;) {
    globalThis.crypto.getRandomValues(buffer);
    const draw = buffer.at(0) ?? 0;
    if (draw < limit) return draw % KEY_SPACE;
  }
}

/**
 * The `k` prefix keeps a core from being read as a number a translator might "tidy up"
 * in a spreadsheet. Uniqueness within a stamp is the allocator's job, since the key it
 * has to check is the surfaced one.
 */
export function mintElementCore(): string {
  return KEY_PREFIX + randomKeyIndex().toString(BASE).padStart(KEY_LENGTH, "0");
}
